import {
  IonCard,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonList,
  useIonRouter,
} from "@ionic/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import { checkmarkCircleOutline } from "ionicons/icons";
import { SearchField } from "@/components/atoms/search-field";
import { EmptyState } from "@/components/molecules/empty-state";
import { NumberTicker } from "@/components/molecules/magicui/number-ticker";
import { CompletedShiftCardRework } from "@/components/molecules/shift/completed-shift-card.rework";
import { api } from "@/lib/backend";
import { type Shift } from "@/lib/backend/api";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useExtractedQuery } from "@/lib/backend/utils/hooks";
import { useMe } from "@/lib/hooks/use-me";
import { PERIOD_SELECT_OPTIONS, type PeriodSelectOption } from "@/lib/period-select";
import { useOutstandingHours } from "@/lib/query/shift/use-outstanding-hours";
import { Routes } from "@/lib/router";
import { type EntryState, STATUSES_BY_STATE, toEntryState } from "@/lib/shift/entry-status";
import { needsHoursSubmission } from "@/lib/shift/hours-submission";
import { calculateShiftDuration } from "@/utils/time";

const ITEMS_PER_PAGE = 10;

/** What "toon alles" resets the period to — the only option that cannot itself hide something. */
const WIDEST_PERIOD: PeriodSelectOption = "all-years";

/**
 * A hairline of the state's own colour on the unselected chip, so "Afgewezen" is findable in the
 * strip without shouting. Selected chips go solid ink like the period chips next to them — the
 * strip is a filter first and a status second.
 */
const STATE_CHIP_BORDER: Record<EntryState, string> = {
  pending: "border-warning-100",
  rejected: "border-error-100",
  approved: "border-success-100",
};

/**
 * The same three fields the server-side search filters on, applied locally. The action list is not
 * fetched per search term, so it has to narrow itself.
 */
function matchesSearch(shift: Shift, search: string): boolean {
  const needle = search.toLowerCase();
  return [shift.project?.name, shift.userFunction?.name, shift.position?.name].some((name) =>
    name?.toLowerCase().includes(needle),
  );
}

/**
 * Everything about finished shifts, in one place.
 *
 * There used to be two: a rich page at /app/shifts/completed with statistics, a period filter,
 * search and a "still to submit" list — which almost nothing linked to — and a plainer version as a
 * sub-tab of Diensten, built from different components. One concept, two designs, two codepaths.
 * This is the single implementation; both entry points render it.
 *
 * The period lives in local state rather than a search param, because this now renders in two
 * places and only one of them owns a route.
 */
export function CompletedShiftsView() {
  const { t } = useTranslation();
  const router = useIonRouter();
  const { me } = useMe();
  const [period, setPeriod] = useState<PeriodSelectOption>("this-month");
  const [searchText, setSearchText] = useState("");
  const [selectedState, setSelectedState] = useState<EntryState | null>(null);

  // One timestamp for the life of this view. It ends up inside the query key, so a live
  // `new Date()` would build a different key on every render and refetch forever. The screen
  // already refetches on view-enter, so freezing it at mount costs nothing worth having.
  const [now] = useState(() => new Date());

  const selectedPeriod = PERIOD_SELECT_OPTIONS.find((option) => option.value === period)!.filter(
    new Date(),
  );

  // Free-text search across project, function and position names. The app's ShiftFilterKeys
  // only exposes these string keys; opdrachtgever/vestiging would need extra api filter keys.
  const search = searchText.trim();
  const searchFilter = search
    ? [
        {
          OR: [
            { "project.name": { operator: "contains" as const, value: search } },
            { "userFunction.name": { operator: "contains" as const, value: search } },
            { "position.name": { operator: "contains" as const, value: search } },
          ],
        },
      ]
    : [];

  const baseFilter = [
    {
      AND: [
        { employeeId: { operator: "equals" as const, value: me?.employee?.id } },
        // Afgerond means worked, not "falls in a month". Without this the screen was bounded by
        // the period alone, so the default chip — deze maand — listed shifts still to come, under
        // a heading that says they are behind you, with a button to submit hours for a night that
        // has not happened. `this-year` used to dodge it by ending its range at "now"; that is now
        // one rule in one place instead of a workaround in one of four options.
        { endAt: { operator: "lt" as const, value: now.toISOString() } },
        { startAt: { operator: "gte" as const, value: selectedPeriod.from.toISOString() } },
        { startAt: { operator: "lte" as const, value: selectedPeriod.to.toISOString() } },
      ],
    },
    ...searchFilter,
  ];

  // The status filter narrows the list but never the statistics — the counts on the strip have to
  // keep describing the whole period, or selecting one would zero out the others.
  //
  // No cast here on purpose: `STATUSES_BY_STATE` holds exactly the members the api's enum has, so
  // the compiler is the thing that catches it if the two drift apart again. It went the other way
  // once — a cast papered over two statuses the api had dropped, which would have sent this filter
  // a value the enum no longer accepts.
  const statusFilter = selectedState
    ? [
        {
          // Wrapped in AND because an entry in the filter array has to be a logical group, not a
          // bare key — the same shape the period clause above uses.
          AND: [
            {
              "shiftTimeEntry.status": {
                operator: "in" as const,
                values: [...STATUSES_BY_STATE[selectedState]],
              },
            },
          ],
        },
      ]
    : [];

  const listFilter = [...baseFilter, ...statusFilter];

  // Whether anything the reader set is hiding shifts. The period counts: it starts on "deze maand",
  // so someone who last worked in June and opens this in August is told they have no completed
  // shifts at all — the emptiest of dead ends, and one nobody chose.
  const isNarrowed = !!search || !!selectedState || period !== WIDEST_PERIOD;

  const { data: statistics } = useExtractedQuery({
    queryKey: [
      "shifts",
      "stats",
      {
        filter: baseFilter,
        pagination: { take: 9999, page: 1 },
        include: { project: { select: true }, shiftTimeEntry: { select: true } },
      } satisfies Parameters<typeof api.shift.getAll>[0],
    ] as const,
    queryFn: (context) => api.shift.getAll(context.queryKey[2], { signal: context.signal }),
    staleTime: Infinity,
    select: (data) =>
      data.items?.reduce(
        (acc, shift) => {
          acc.totalAmount += 1;

          const startAt =
            typeof shift.shiftTimeEntry?.startAt === "string"
              ? shift.shiftTimeEntry.startAt
              : shift.startAt;
          const endAt = shift.shiftTimeEntry?.endAt ?? shift.endAt;
          const time = calculateShiftDuration(
            startAt,
            endAt,
            shift.shiftTimeEntry?.breakTimeMinutes ?? 0,
          );

          acc.totalMinutes += time.minutes;
          acc.totalHours += time.hours;

          if (acc.totalMinutes >= 60) {
            acc.totalHours += Math.floor(acc.totalMinutes / 60);
            acc.totalMinutes = acc.totalMinutes % 60;
          }

          // Where this period stands. Counted over every shift in it rather than the page on
          // screen, so the strip and the list can disagree about *what is visible* but never
          // about what exists.
          const state = toEntryState(shift.shiftTimeEntry?.status);
          if (state) {
            acc.byState[state] += 1;
          }

          return acc;
        },
        {
          totalAmount: 0,
          totalHours: 0,
          totalMinutes: 0,
          byState: { pending: 0, rejected: 0, approved: 0 } as Record<EntryState, number>,
        },
      ),
  });

  // Rejected first: it is the only one that wants something from you. The rest follow the
  // lifecycle. A state with nothing in it does not get a chip — an empty "Afgewezen 0" is a
  // question nobody asked.
  const stateOrder: EntryState[] = ["rejected", "pending", "approved"];
  const visibleStates = stateOrder.filter((state) => (statistics?.byState[state] ?? 0) > 0);

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: [
      "shifts",
      {
        filter: listFilter,
        sort: { field: "startAt", order: "desc" },
        pagination: { take: ITEMS_PER_PAGE },
      } satisfies Parameters<typeof api.shift.getAll>[0],
    ] as const,
    queryFn: (context) =>
      extractionWrapper(
        api.shift.getAll(
          {
            ...context.queryKey[1],
            pagination: { ...context.queryKey[1].pagination, page: context.pageParam + 1 },
            include: { project: { select: true }, shiftTimeEntry: { select: true } },
          },
          { signal: context.signal },
        ),
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      (lastPageParam + 1) * ITEMS_PER_PAGE >= lastPage.totalAmount ? undefined : lastPageParam + 1,
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => firstPageParam - 1,
    select: (result) => result.pages.flatMap((page) => page.items).filter(Boolean),
  });

  // The action list comes from the same query as the badge that counts it, not from the page of
  // history below it. Built from `data` it inherited two bounds that have nothing to do with
  // owing hours: the selected period (so "deze maand" hid a July backlog while the badge still
  // counted it) and the current page (so the rest appeared only once you scrolled).
  //
  // It deliberately ignores the period for the same reason — what you still owe is not a
  // property of the month you happen to be looking at. Search does apply: if you are looking
  // for one project, you mean this list too.
  //
  // Picking a status hides it: "nog indienen" is not one of the four states, so leaving it above
  // a list filtered to "Goedgekeurd" would answer a question you did not ask.
  const outstanding = useOutstandingHours();
  const searchedOutstanding = search
    ? outstanding.shifts.filter((shift) => matchesSearch(shift, search))
    : outstanding.shifts;
  const shiftsToDeclare = selectedState ? [] : searchedOutstanding;

  // Without a status filter the two sections are complements and every loaded shift lands in one
  // of them. With one, the action list is empty by design — so splitting on `needsHoursSubmission`
  // would drop any match that also still owes hours (a PENDING entry with no break time, say)
  // into neither section. It would simply stop existing on screen, with no empty state to explain
  // it, because other rows were still there.
  const shiftsCompleted = selectedState
    ? (data ?? [])
    : (data?.filter((shift) => !needsHoursSubmission(shift)) ?? []);

  const loadMore = async (ev: CustomEvent) => {
    await fetchNextPage();
    if (ev.target) {
      (ev.target as any).complete();
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-3 *:flex-1">
        <IonCard className="flex flex-col items-center justify-center px-4 py-6">
          <NumberTicker className="text-2xl font-bold" value={statistics?.totalAmount ?? 0} />
          <span className="text-sm font-medium text-txt-secondary">
            {t("routes:completed-shifts.statistics.shifts")}
          </span>
        </IonCard>
        <IonCard className="flex flex-col items-center justify-center px-4 py-6">
          <NumberTicker className="text-2xl font-bold" value={statistics?.totalHours ?? 0} />
          <span className="text-sm font-medium text-txt-secondary">
            {t("routes:completed-shifts.statistics.hours")}
          </span>
        </IonCard>
      </div>

      {/* Where this period stands. The two cards above say how much you worked; this says how
          much of it is settled — the question "wat is goedgekeurd en wat niet", answered for the
          whole period rather than one row at a time. Filtering happens server-side on
          `shiftTimeEntry.status`, so it reaches past what is loaded. */}
      {visibleStates.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedState(null)}
            className={`rounded-full border border-solid px-3 py-1 text-sm font-medium transition-opacity active:opacity-70 ${
              selectedState === null
                ? "border-txt-primary bg-txt-primary text-txt-inverted"
                : "border-bordercolor-primary text-txt-secondary"
            }`}
          >
            {t("routes:completed-shifts.status-filter.all")}{" "}
            <span className="tabular-nums opacity-70">{statistics?.totalAmount ?? 0}</span>
          </button>

          {visibleStates.map((state) => (
            <button
              key={state}
              type="button"
              onClick={() => setSelectedState(selectedState === state ? null : state)}
              className={`rounded-full border border-solid px-3 py-1 text-sm font-medium transition-opacity active:opacity-70 ${
                selectedState === state
                  ? "border-txt-primary bg-txt-primary text-txt-inverted"
                  : `${STATE_CHIP_BORDER[state]} text-txt-secondary`
              }`}
            >
              {t(`shift:entry-status.state.${state}`)}{" "}
              <span className="tabular-nums opacity-70">{statistics?.byState[state]}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {PERIOD_SELECT_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setPeriod(option.value)}
            className={`rounded-full border border-solid px-3.5 py-1.5 text-sm font-medium transition-opacity active:opacity-70 ${
              period === option.value
                ? "border-txt-primary bg-txt-primary text-txt-inverted"
                : "border-bordercolor-primary text-txt-secondary"
            }`}
          >
            {t(option.label)}
          </button>
        ))}
      </div>

      <SearchField
        value={searchText}
        onChange={setSearchText}
        placeholder={t("common:search.placeholder")}
        debounce={300}
      />

      {shiftsToDeclare.length > 0 && (
        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-xs font-bold uppercase tracking-wide text-txt-secondary">
              {t("routes:completed-shifts.action-list")}
            </h2>
            <p className="text-xs text-txt-tertiary">
              {t("routes:completed-shifts.action-list-hint")}
            </p>
          </div>
          <IonList className="flex flex-col gap-1">
            {shiftsToDeclare.map((shift) => (
              <CompletedShiftCardRework key={shift.id} shift={shift} />
            ))}
          </IonList>
        </section>
      )}

      {shiftsCompleted.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-xs font-bold uppercase tracking-wide text-txt-secondary">
            {t("routes:completed-shifts.latest-shifts")}
          </h2>
          <IonList className="flex flex-col gap-1">
            {shiftsCompleted.map((shift) => (
              <CompletedShiftCardRework key={shift.id} shift={shift} />
            ))}
          </IonList>
        </section>
      )}

      {/* Only claim emptiness once we know — never mid-flight on a cold open. Both queries have
          to have landed, or a slow one turns into "geen afgeronde diensten". */}
      {!isLoading &&
        !outstanding.isLoading &&
        shiftsToDeclare.length === 0 &&
        shiftsCompleted.length === 0 && (
          <div className="flex flex-col items-center gap-4">
            <EmptyState
              icon={checkmarkCircleOutline}
              title={
                search
                  ? t("routes:upcoming-shifts.empty-completed.no-results")
                  : t("routes:upcoming-shifts.empty-completed.title")
              }
              description={
                search
                  ? t("routes:upcoming-shifts.empty-completed.no-results-description")
                  : t("routes:upcoming-shifts.empty-completed.description")
              }
            />

            {/* Two different nothings, so two different ways out.

                Narrowed to nothing is the common one and the easy one to miss: the period starts on
                "deze maand", so someone who worked in June and opens this in August is told they
                have no completed shifts at all. Clearing the narrowing is the answer, not a trip to
                Ontdek.

                Genuinely nothing worked yet is the other, and there the only useful next step is
                finding work. Offering "toon alles" there would just reload the same empty screen. */}
            {isNarrowed ? (
              <button
                type="button"
                onClick={() => {
                  setSearchText("");
                  setSelectedState(null);
                  setPeriod(WIDEST_PERIOD);
                }}
                className="rounded-xl border-2 border-solid border-button-primary px-5 py-3 text-md font-semibold leading-[1.5] text-txt-primary"
              >
                {t("routes:upcoming-shifts.empty-completed.reset")}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => router.push(Routes.Projects(), "forward")}
                className="rounded-xl border-2 border-solid border-button-primary bg-button-primary px-5 py-3 text-md font-semibold leading-[1.5] text-txt-inverted"
              >
                {t("routes:upcoming-shifts.empty-completed.cta")}
              </button>
            )}
          </div>
        )}

      <IonInfiniteScroll onIonInfinite={loadMore} disabled={!hasNextPage}>
        <IonInfiniteScrollContent loadingText={t("common:loading")} loadingSpinner="bubbles" />
      </IonInfiniteScroll>
    </div>
  );
}
